/**
 * Scott & Burgan (2005) standard fire-behaviour fuel models.
 *
 * Numbers 101..204 are the canonical extended set used by the
 * RothermelSBModel in the backend fire simulation.  They map onto our
 * ``model_subclass`` column and are only meaningful when the polygon's
 * ``model_class`` is ``20`` (Scott & Burgan).
 *
 * Keep this list in sync with ``fire_spread_new/fuel_models/scott_burgan_table.py``.
 */

export interface ScottBurganFuelModel {
  /** Numeric id stored in ``model_subclass``. */
  number: number;
  /** Short code, e.g. ``GR2``. */
  code: string;
  /** Family label for grouping in dropdowns. */
  family:
    | "Grass"
    | "Grass-Shrub"
    | "Shrub"
    | "Timber-Understory"
    | "Timber Litter"
    | "Slash-Blowdown"
    | "Non-Burnable";
  /** Short human description. */
  description: string;
}

export const SCOTT_BURGAN_FUEL_MODELS: ScottBurganFuelModel[] = [
  // Grass (dynamic)
  { number: 101, code: "GR1", family: "Grass", description: "Short, sparse dry climate grass" },
  { number: 102, code: "GR2", family: "Grass", description: "Low load, dry climate grass" },
  { number: 103, code: "GR3", family: "Grass", description: "Low load, very coarse humid climate grass" },
  { number: 104, code: "GR4", family: "Grass", description: "Moderate load, dry climate grass" },
  { number: 105, code: "GR5", family: "Grass", description: "Low load, humid climate grass" },
  { number: 106, code: "GR6", family: "Grass", description: "Moderate load, humid climate grass" },
  { number: 107, code: "GR7", family: "Grass", description: "High load, dry climate grass" },
  { number: 108, code: "GR8", family: "Grass", description: "High load, very coarse humid climate grass" },
  { number: 109, code: "GR9", family: "Grass", description: "Very high load, humid climate grass" },

  // Grass-Shrub (dynamic)
  { number: 121, code: "GS1", family: "Grass-Shrub", description: "Low load, dry climate grass-shrub" },
  { number: 122, code: "GS2", family: "Grass-Shrub", description: "Moderate load, dry climate grass-shrub" },
  { number: 123, code: "GS3", family: "Grass-Shrub", description: "Moderate load, humid climate grass-shrub" },
  { number: 124, code: "GS4", family: "Grass-Shrub", description: "High load, humid climate grass-shrub" },

  // Shrub
  { number: 141, code: "SH1", family: "Shrub", description: "Low load, dry climate shrub" },
  { number: 142, code: "SH2", family: "Shrub", description: "Moderate load, dry climate shrub" },
  { number: 143, code: "SH3", family: "Shrub", description: "Moderate load, humid climate shrub" },
  { number: 144, code: "SH4", family: "Shrub", description: "Low load, humid climate timber-shrub" },
  { number: 145, code: "SH5", family: "Shrub", description: "High load, dry climate shrub" },
  { number: 146, code: "SH6", family: "Shrub", description: "Low load, humid climate shrub" },
  { number: 147, code: "SH7", family: "Shrub", description: "Very high load, dry climate shrub" },
  { number: 148, code: "SH8", family: "Shrub", description: "High load, humid climate shrub" },
  { number: 149, code: "SH9", family: "Shrub", description: "Very high load, humid climate shrub" },

  // Timber-Understory
  { number: 161, code: "TU1", family: "Timber-Understory", description: "Low load, dry climate timber-grass-shrub" },
  { number: 162, code: "TU2", family: "Timber-Understory", description: "Moderate load, humid climate timber-shrub" },
  { number: 163, code: "TU3", family: "Timber-Understory", description: "Moderate load, humid climate timber-grass-shrub" },
  { number: 164, code: "TU4", family: "Timber-Understory", description: "Dwarf conifer with understory" },
  { number: 165, code: "TU5", family: "Timber-Understory", description: "Very high load, dry climate timber-shrub" },

  // Timber Litter
  { number: 181, code: "TL1", family: "Timber Litter", description: "Low load, compact conifer litter" },
  { number: 182, code: "TL2", family: "Timber Litter", description: "Low load, broadleaf litter" },
  { number: 183, code: "TL3", family: "Timber Litter", description: "Moderate load conifer litter" },
  { number: 184, code: "TL4", family: "Timber Litter", description: "Small downed logs" },
  { number: 185, code: "TL5", family: "Timber Litter", description: "High load conifer litter" },
  { number: 186, code: "TL6", family: "Timber Litter", description: "Moderate load broadleaf litter" },
  { number: 187, code: "TL7", family: "Timber Litter", description: "Large downed logs" },
  { number: 188, code: "TL8", family: "Timber Litter", description: "Long-needle litter" },
  { number: 189, code: "TL9", family: "Timber Litter", description: "Very high load broadleaf litter" },

  // Slash-Blowdown
  { number: 201, code: "SB1", family: "Slash-Blowdown", description: "Low load activity fuel" },
  { number: 202, code: "SB2", family: "Slash-Blowdown", description: "Moderate load activity fuel or low load blowdown" },
  { number: 203, code: "SB3", family: "Slash-Blowdown", description: "High load activity fuel or moderate load blowdown" },
  { number: 204, code: "SB4", family: "Slash-Blowdown", description: "High load blowdown" },
];

export const SCOTT_BURGAN_MODEL_CLASS_NAME = "Scott & Burgan";

export const isScottBurganModelClass = (name?: string | null): boolean =>
  name === SCOTT_BURGAN_MODEL_CLASS_NAME;

const _BY_NUMBER: Record<number, ScottBurganFuelModel> = SCOTT_BURGAN_FUEL_MODELS.reduce(
  (acc, fm) => ({ ...acc, [fm.number]: fm }),
  {},
);

export const getScottBurganFuelModel = (
  number: number | null | undefined,
): ScottBurganFuelModel | null => {
  if (number == null) return null;
  return _BY_NUMBER[number] ?? null;
};

/** Short label for display, e.g. ``GR2 · Low load, dry climate grass``. */
export const formatScottBurganLabel = (fm: ScottBurganFuelModel): string =>
  `${fm.code} · ${fm.description}`;
